import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {BadgeCheck, Ban, CheckCircle2, Clock, FileStack, ShieldCheck, TrendingUp} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import StatCard from '@/components/dashboard/StatCard'
import MiniBarChart from '@/components/dashboard/MiniBarChart'
import AlertaAbertos from '@/components/dashboard/AlertaAbertos'
import TratadosPorGrupo from '@/components/dashboard/TratadosPorGrupo'
import { formatDateTimeBR } from '@/utils/masks'
import { buildEscopoFilter, filtrarPorEscopo } from '@/utils/escopo'

interface Protocolo {
  _id: string
  numeroProtocolo: string
  nomeCompleto: string
  status: string
  secretariaId?: string
  subLocalId?: string
  analisadoPorNome?: string
  analisadoEm?: string
  createdAt: string
}

interface SubLocal {
  _id: string
  nome: string
}

interface Secretaria {
  _id: string
  nome: string
}

interface HistoricoItem {
  _id: string
  protocoloId: string
  acao: string
  descricao?: string
  creator?: string
  createdAt: string
}

const statusBadge: Record<string, { label: string, cls: string }> = {
  AGUARDANDO_ANALISE: { label: 'Aguardando análise', cls: 'bg-[#FEF9E7] text-[#B7791F]' },
  APROVADO: { label: 'Aprovado', cls: 'bg-[#EAF7EE] text-[#1E8A45]' },
  APROVADO_COM_ABONO: { label: 'Aprovado com abono', cls: 'bg-[#EEF3FE] text-[#1E4FBF]' },
  APROVADO_SEM_ABONO: { label: 'Aprovado sem abono', cls: 'bg-[#F1F2F4] text-[#4B5563]' },
  REPROVADO: { label: 'Reprovado', cls: 'bg-[#FEF2F2] text-[#B91C1C]' },
}

const acaoLabel: Record<string, string> = {
  CRIADO: 'Requerimento criado',
  APROVADO: 'Aprovado',
  APROVADO_COM_ABONO: 'Aprovado com abono',
  APROVADO_SEM_ABONO: 'Aprovado sem abono',
  REPROVADO: 'Reprovado',
}

const acaoDot: Record<string, string> = {
  CRIADO: 'bg-[#1E4FBF]',
  APROVADO: 'bg-[#1E8A45]',
  APROVADO_COM_ABONO: 'bg-[#1E4FBF]',
  APROVADO_SEM_ABONO: 'bg-[#6B7280]',
  REPROVADO: 'bg-[#B91C1C]',
}

const MESES_ABREV = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

const roleLabel: Record<string, string> = {
  ADMIN: 'Administrador',
  MODERADOR: 'Moderador',
  GESTOR: 'Gestor',
  COORDENADOR: 'Coordenador',
}

function Dashboard() {
  const { user } = useAppAuth()
  const navigate = useNavigate()
  const [protocolos, setProtocolos] = useState<Protocolo[]>([])
  const [secretarias, setSecretarias] = useState<Secretaria[]>([])
  const [sublocais, setSublocais] = useState<SubLocal[]>([])
  const [historico, setHistorico] = useState<HistoricoItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const filter: Record<string, any> = buildEscopoFilter(user)

        const [protRes, secRes, histRes, subRes] = await Promise.all([
          (lumi.entities as any).protocolos.list({
            filter,
            sort: { createdAt: -1 },
            limit: 200,
          }),
          (lumi.entities as any).secretarias.list({ limit: 200 }),
          (lumi.entities as any).protocolo_historico.list({
            sort: { createdAt: -1 },
            limit: 8,
          }),
          (lumi.entities as any).sublocais.list({ limit: 200 }),
        ])
        setProtocolos(filtrarPorEscopo(protRes?.list || [], user))
        setSecretarias(secRes?.list || [])
        setHistorico(histRes?.list || [])
        setSublocais(subRes?.list || [])
      }
      catch {
        setProtocolos([])
        setSecretarias([])
        setHistorico([])
        setSublocais([])
      }
      finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [user?._id, user?.role, user?.cidadeId, user?.secretariaId])

  const total = protocolos.length
  const aguardando = protocolos.filter(p => p.status === 'AGUARDANDO_ANALISE').length
  const aprovados = protocolos.filter(p => p.status.startsWith('APROVADO')).length
  const comAbono = protocolos.filter(p => p.status === 'APROVADO_COM_ABONO').length
  const semAbono = protocolos.filter(p => p.status === 'APROVADO_SEM_ABONO').length
  const reprovados = protocolos.filter(p => p.status === 'REPROVADO').length
  const analisados = aprovados + reprovados
  const taxaAprovacao = analisados > 0 ? Math.round((aprovados / analisados) * 100) : 0

  const primeiroNome = useMemo(() => (user?.nomeCompleto || '').split(' ')[0] || '', [user])

  const secretariaNomeMap = useMemo(() => {
    const map: Record<string, string> = {}
    secretarias.forEach((s) => { map[s._id] = s.nome })
    return map
  }, [secretarias])

  const subLocalNomeMap = useMemo(() => {
    const map: Record<string, string> = {}
    sublocais.forEach((s) => { map[s._id] = s.nome })
    return map
  }, [sublocais])

  const abertos = useMemo(
    () => protocolos.filter(p => p.status === 'AGUARDANDO_ANALISE'),
    [protocolos],
  )

  const tratados = useMemo(
    () => protocolos.filter(p => p.status !== 'AGUARDANDO_ANALISE'),
    [protocolos],
  )

  const dadosMensais = useMemo(() => {
    const hoje = new Date()
    const meses: { key: string, label: string, value: number }[] = []
    for (let i = 5; i >= 0; i--) {
      const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1)
      meses.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: MESES_ABREV[d.getMonth()], value: 0 })
    }
    protocolos.forEach((p) => {
      const d = new Date(p.createdAt)
      if (Number.isNaN(d.getTime())) return
      const key = `${d.getFullYear()}-${d.getMonth()}`
      const item = meses.find(m => m.key === key)
      if (item) item.value += 1
    })
    return meses.map(({ label, value }) => ({ label, value }))
  }, [protocolos])

  const dadosPorSecretaria = useMemo(() => {
    const contagem: Record<string, number> = {}
    protocolos.forEach((p) => {
      const nome = secretariaNomeMap[p.secretariaId || ''] || 'Sem secretaria'
      contagem[nome] = (contagem[nome] || 0) + 1
    })
    return Object.entries(contagem)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([label, value]) => ({ label, value }))
  }, [protocolos, secretariaNomeMap])

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-[20px] sm:text-[22px] font-semibold text-[#12141A] leading-tight">
            Olá
            {primeiroNome ? `, ${primeiroNome}` : ''}
            {' '}
            👋
          </h1>
          <p className="text-[12.5px] sm:text-[13.5px] text-[#6B7280] mt-1">
            {user?.role ? `${roleLabel[user.role] || user.role} • ` : ''}
            Aqui está o resumo dos requerimentos do sistema.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-[#E7E9EC] rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 shrink-0 self-start sm:self-auto">
          <div className="w-9 h-9 rounded-lg bg-[#EAF7EE] flex items-center justify-center">
            <TrendingUp className="w-4.5 h-4.5 text-[#1E8A45]" strokeWidth={1.75} />
          </div>
          <div>
            <p className="text-[16px] font-semibold text-[#12141A] leading-tight tabular-nums">
              {taxaAprovacao}
              %
            </p>
            <p className="text-[11px] text-[#9CA3AF]">Taxa de aprovação</p>
          </div>
        </div>
      </div>

      <AlertaAbertos
        abertos={abertos}
        secretariaNomeMap={secretariaNomeMap}
        loading={loading}
        onVerTodos={() => navigate('/protocolos')}
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2.5 sm:gap-4">
        <StatCard label="Requerimentos enviados" value={total} icon={FileStack} accent="blue" />
        <StatCard label="Aguardando análise" value={aguardando} icon={Clock} accent="yellow" />
        <StatCard label="Aprovados" value={aprovados} icon={CheckCircle2} accent="green" />
        <StatCard label="Aprovados com abono" value={comAbono} icon={ShieldCheck} accent="orange" />
        <StatCard label="Aprovados sem abono" value={semAbono} icon={BadgeCheck} accent="gray" />
        <StatCard label="Reprovados" value={reprovados} icon={Ban} accent="red" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-[#E7E9EC] p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <div>
              <h2 className="text-[14px] sm:text-[14.5px] font-semibold text-[#12141A]">Requerimentos por mês</h2>
              <p className="text-[12px] text-[#9CA3AF] mt-0.5">Volume dos últimos 6 meses</p>
            </div>
            <span className="w-2 h-2 rounded-full bg-[#1E4FBF]" />
          </div>
          {loading
            ? <div className="h-[200px] animate-pulse bg-[#F7F8FA] rounded-xl" />
            : <MiniBarChart data={dadosMensais} color="#1E4FBF" />}
        </div>

        <div className="bg-white rounded-2xl border border-[#E7E9EC] p-4 sm:p-5">
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <div>
              <h2 className="text-[14px] sm:text-[14.5px] font-semibold text-[#12141A]">Requerimentos por secretaria</h2>
              <p className="text-[12px] text-[#9CA3AF] mt-0.5">Top secretarias com mais requerimentos</p>
            </div>
            <span className="w-2 h-2 rounded-full bg-[#D9622B]" />
          </div>
          {loading
            ? <div className="h-[200px] animate-pulse bg-[#F7F8FA] rounded-xl" />
            : <MiniBarChart data={dadosPorSecretaria} color="#D9622B" emptyLabel="Ainda não há requerimentos vinculados a secretarias." />}
        </div>
      </div>

      <TratadosPorGrupo
        tratados={tratados}
        secretariaNomeMap={secretariaNomeMap}
        subLocalNomeMap={subLocalNomeMap}
        loading={loading}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E7E9EC] p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[14px] sm:text-[14.5px] font-semibold text-[#12141A]">Últimos requerimentos</h2>
            <span className="text-[11.5px] text-[#9CA3AF]">
              {protocolos.length}
              {' '}
              no total
            </span>
          </div>
          {loading
            ? (
                <div className="flex flex-col gap-3 py-1">
                  {[0, 1, 2].map(i => <div key={i} className="h-12 rounded-xl bg-[#F7F8FA] animate-pulse" />)}
                </div>
              )
            : protocolos.length === 0
              ? (
                  <div className="py-10 flex flex-col items-center justify-center text-center">
                    <div className="w-11 h-11 rounded-xl bg-[#F1F2F4] flex items-center justify-center mb-3">
                      <FileStack className="w-5 h-5 text-[#9CA3AF]" strokeWidth={1.75} />
                    </div>
                    <p className="text-[13px] text-[#9CA3AF]">Ainda não existem requerimentos registados.</p>
                  </div>
                )
              : (
                  <div className="flex flex-col divide-y divide-[#F1F2F4]">
                    {protocolos.slice(0, 6).map(p => (
                      <div key={p._id} className="flex items-start sm:items-center justify-between gap-2 py-3 first:pt-0 last:pb-0 group">
                        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-[#F7F8FA] flex items-center justify-center text-[11.5px] font-semibold text-[#4B5563] shrink-0 group-hover:bg-[#EEF3FE] group-hover:text-[#1E4FBF] transition-colors">
                            {(p.nomeCompleto || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[13px] sm:text-[13.5px] font-medium text-[#12141A] truncate">{p.nomeCompleto}</p>
                            <p className="text-[11px] sm:text-[12px] text-[#9CA3AF] truncate">
                              Nº
                              {' '}
                              {p.numeroProtocolo}
                              {' '}
                              •
                              {' '}
                              {formatDateTimeBR(p.createdAt)}
                            </p>
                          </div>
                        </div>
                        <span className={`text-[10.5px] sm:text-[11.5px] font-medium px-2 sm:px-2.5 py-1 rounded-full shrink-0 ${statusBadge[p.status]?.cls || 'bg-[#F1F2F4] text-[#4B5563]'}`}>
                          {statusBadge[p.status]?.label || p.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
        </div>

        <div className="bg-white rounded-2xl border border-[#E7E9EC] p-4 sm:p-5">
          <h2 className="text-[14px] sm:text-[14.5px] font-semibold text-[#12141A] mb-3">Atividade recente</h2>
          {loading
            ? (
                <div className="flex flex-col gap-3 py-1">
                  {[0, 1, 2].map(i => <div key={i} className="h-12 rounded-xl bg-[#F7F8FA] animate-pulse" />)}
                </div>
              )
            : historico.length === 0
              ? <p className="text-[13px] text-[#9CA3AF] py-6 text-center">Sem atividades recentes.</p>
              : (
                  <div className="flex flex-col">
                    {historico.map((h, idx) => (
                      <div key={h._id} className="relative flex gap-3 pb-4 last:pb-0">
                        {idx !== historico.length - 1 && (
                          <span className="absolute left-[5px] top-3 bottom-0 w-px bg-[#F1F2F4]" />
                        )}
                        <span className={`relative z-10 w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${acaoDot[h.acao] || 'bg-[#9CA3AF]'}`} />
                        <div className="min-w-0">
                          <p className="text-[13px] font-medium text-[#12141A]">
                            {acaoLabel[h.acao] || h.acao}
                          </p>
                          {h.descricao
                            ? <p className="text-[12px] text-[#6B7280] line-clamp-2 mt-0.5">{h.descricao}</p>
                            : null}
                          <p className="text-[11.5px] text-[#9CA3AF] mt-0.5">{formatDateTimeBR(h.createdAt)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
